import React, { useCallback, useState } from 'react'
import { Link } from '@/components/Link'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import classNames from 'classnames'
import { LinkIcon } from 'components/Icon'
import { Loading } from 'components/Loading'
import styled from 'styled-components'
import { useFetchOne } from 'utils/api/useFetch'
import { WorldCheckApiClient } from 'utils/api/worldCheckApi'
import { formatLocalDateUTC } from 'utils/date'

import { WorldCheckLogo } from './WorldCheckLogo'

const Wrapper = styled.div`
  display: flex;
  margin: 0 0 2.5rem 0;
  width: 100%;
`
const Container = styled.div`
  min-height: 30rem;
  width: 100%;
  display: flex;
  flex-direction: column;

  header {
    display: flex;
  }

  menu {
    margin: 0;
    padding-left: 1rem;
    background-color: var(--background);

    .menu-item {
      padding: 0.5rem;
      border-bottom: 1px solid var(--background);
      cursor: pointer;

      &:hover {
        opacity: 0.7;
      }

      &-active {
        text-decoration: underline;
      }
    }
  }

  ul {
    list-style-type: none;
    width: 100%;
    display: flex;
    padding-left: 0;
    flex-direction: column;
  }

  li {
    width: 100%;
    border-bottom: 1px solid var(--background);
    color: var(--foreground);

    > label {
      width: 25%;
      color: var(--foreground);
      font-weight: bold;
    }

    > span {
      padding-left: 1rem;
      width: 75%;
      color: #0e76eb;
    }
  }

  nav {
    display: flex;
    justify-content: space-between;
    background-color: #e2e2ea;
    padding: 0.5rem;
  }

  .content {
    width: 100%;
  }
`

interface Props {
  referenceId: string
  worldCheckApiClient: WorldCheckApiClient
  onClose(): void
}

export const ClientDetailsWorldCheckResultDetails: React.FC<Props> = ({
  referenceId,
  onClose,
  worldCheckApiClient,
}) => {
  const fetchData = useCallback(
    () => worldCheckApiClient.getWorldCheckDetailsDataByReferenceId(referenceId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [referenceId]
  )
  const { data, isLoading } = useFetchOne(fetchData)

  const [currentItem, setCurrentItem] = useState<
    'key_data' | 'further_info' | 'aliases' | 'keywords' | 'sources'
  >('key_data')
  const address = data?.addresses[0]
  const details = data?.details
  const names = data?.names
  const sources = data?.sources
  const weblinks = data?.weblinks

  return (
    <Container>
      <NavigationHeader onGoBack={onClose}>
        <WorldCheckLogo />
      </NavigationHeader>

      <Wrapper>
        <menu>
          <div
            className={classNames('menu-item', { 'menu-item-active': currentItem === 'key_data' })}
            onClick={() => setCurrentItem('key_data')}
          >
            Key Data
          </div>
          <div
            className={classNames('menu-item', {
              'menu-item-active': currentItem === 'further_info',
            })}
            onClick={() => setCurrentItem('further_info')}
          >
            Further Information
          </div>
          <div
            className={classNames('menu-item', { 'menu-item-active': currentItem === 'aliases' })}
            onClick={() => setCurrentItem('aliases')}
          >
            Aliases
          </div>
          <div
            className={classNames('menu-item', { 'menu-item-active': currentItem === 'keywords' })}
            onClick={() => setCurrentItem('keywords')}
          >
            Keywords
          </div>
          <div
            className={classNames('menu-item', { 'menu-item-active': currentItem === 'sources' })}
            onClick={() => setCurrentItem('sources')}
          >
            Sources
          </div>
        </menu>
        <Loading isLoading={isLoading}>
          {data && (
            <div className='content'>
              <nav>
                <span>Name</span>
                {/*<span>{name ? name.fullName : ''}</span>*/}
                {/*<span>[alias] {names ? names[0].fullName : ''}</span>*/}
              </nav>
              {currentItem === 'key_data' && (
                <ul>
                  <li>
                    <label>Name</label>
                    <span>{data.names[0].fullName}</span>
                  </li>
                  <li>
                    <label>Update Categorization</label>
                    <span>{data.updateCategory}</span>
                  </li>
                  <li>
                    <label>Entered Date</label>
                    <span>{formatLocalDateUTC(data.creationDate)}</span>
                  </li>
                  <li>
                    <label>Update Date</label>
                    <span>{formatLocalDateUTC(data.modificationDate)}</span>
                  </li>
                  <li>
                    <label>Category</label>
                    <span>{data.category}</span>
                  </li>
                  <li>
                    <label>Gender</label>
                    <span>{data.gender}</span>
                  </li>
                  <li>
                    <label>Date Of Birth</label>
                    <span>
                      {(data.events || []).map((event, i: number) => (
                        <p key={i}>
                          {event.day != null ? event.day + '-' : ''}
                          {event.month != null ? event.month + '-' : ''}
                          {event.year != null ? event.year : ''}
                        </p>
                      ))}
                    </span>
                  </li>
                  <li>
                    <label>Nationality</label>
                    <span>{data.countryLinks[0].country.name}</span>
                  </li>
                  <li>
                    <label>Street</label>
                    <span>
                      {typeof address?.street === 'object'
                        ? address.street?.valueKind
                        : address?.street}
                    </span>
                  </li>
                  <li>
                    <label>City</label>
                    <span>
                      {typeof address?.city === 'object' ? address.city?.valueKind : address?.city}
                    </span>
                  </li>
                  <li>
                    <label>Region</label>
                    <span>
                      {typeof address?.region === 'object'
                        ? address.region?.valueKind
                        : address?.region}
                    </span>
                  </li>
                  <li>
                    <label>Post Code</label>
                    <span>
                      {typeof address?.postCode === 'object'
                        ? address.postCode?.valueKind
                        : address?.postCode}
                    </span>
                  </li>
                  <li>
                    <label>Country</label>
                    <span>
                      {address?.country?.name} ({address?.country?.code})
                    </span>
                  </li>
                </ul>
              )}
              {currentItem === 'further_info' && (
                <ul>
                  {(details || []).map((detail) => (
                    <li key={detail.detailType}>
                      <label>{detail.detailType}</label>
                      <span>{detail?.text}</span>
                    </li>
                  ))}
                </ul>
              )}
              {currentItem === 'aliases' && (
                <ul>
                  {(names || []).map((alias, i) => (
                    <li key={alias.fullName}>
                      <label>Name ({i + 1})</label>
                      <span>{alias.fullName}</span>
                    </li>
                  ))}
                </ul>
              )}
              {currentItem === 'keywords' && (
                <ul>
                  {(sources || []).map((source, i) => (
                    <div key={i}>
                      <li key={source.abbreviation}>
                        <label>Keyword</label>
                        <span>{source.abbreviation}</span>
                      </li>
                      <li key={source.name}>
                        <label>Description</label>
                        <span>{source.name}</span>
                      </li>
                      <li
                        key={
                          typeof source.regionOfAuthority === 'object'
                            ? source.regionOfAuthority?.valueKind
                            : ''
                        }
                      >
                        <label>Country</label>
                        {typeof source?.regionOfAuthority === 'object'
                          ? source.regionOfAuthority?.valueKind
                          : ''}
                      </li>
                    </div>
                  ))}
                </ul>
              )}
              {currentItem === 'sources' && (
                <ul>
                  {(weblinks || []).map((link, i) => (
                    <li key={i}>
                      <Link title={link.uri} to={link.uri} style={{ marginLeft: '1rem' }}>
                        {link.uri} <LinkIcon />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Loading>
      </Wrapper>
    </Container>
  )
}
